/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Input, Radio, Row, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors} from '../../constants/Common';
import {EfdbName} from '../../constants/efdbNameConstants';
import InformativeText from '../../constants/InformativeText';
import {
  OPKey,
  OPLabel,
  OPplaceholder
} from '../../pages/configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {
  AllCap,
  eachWordCapOrAllCap,
  FirstCap
} from '../../utils/FormUtils/FirstCap';
import RegEx from '../../utils/FormUtils/RegEx';
import PhoneNumberInput from '../CustomInputs/PhoneNumberInput/PhoneNumberInput';
import {yesNo} from '../Table/Constants/Common';

import {facilityKeyLabel, yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {upstreamKey, upstreamLabel} from './Constants/IP/UpstreamKeyLabel';
import {filterSelectOptions} from './Utils/commonFunctions';

const SupplierInfo: React.FC<{
  type: 'up' | 'down';
  data?: any;
  currentStep?: number;
}> = ({type, data = undefined, currentStep}) => {
  const [searchParam] = useSearchParams();
  const [facilityOpt, setFacilityOpt] = useState([]);
  const {products, rawMaterials, capitalGoods} = useJSONStore.getState();
  const supplierInfo = InformativeText?.supplierInfo;
  const FormInstance = useFormInstance();
  const {setFields, setFieldValue, getFieldValue} = FormInstance;
  const countryOptions = useJSONStore((state) => state.country);
  const stateOptions = useJSONStore((state) => state.states);
  const cityOptions = useJSONStore((state) => state.city);
  const facilityDetails = useCompanyStore.getState().facilityDetails;
  const companyDetails = useCompanyStore.getState().companyDetails;
  const [yearOptions, setYearOptions] = useState<any[]>([]);
  const supplierType = useJSONStore((state) => state.supplierType).filter(
    (item) => {
      return (
        item?.type === (type === 'up' ? 'upstream' : 'downstream') &&
        item?.subCategoryId.includes(companyDetails?.[OPKey.SUB_CATEGORY])
      );
    }
  );

  const [country, setCountry] = useState(
    data?.[upstreamKey.COUNTRY] ? data?.[upstreamKey.COUNTRY] : undefined
  );

  const nameFieldLabel = {
    name: upstreamLabel.SUPPLIER_NAME,
    nameType: upstreamLabel.SUPPLIER_TYPE
  };

  useEffect(() => {
    setFacilityOpt(
      facilityDetails?.[searchParam.get('selectedYear') ?? '']?.filter(
        (item: any) => {
          return !item.isLeased;
        }
      ) ?? []
    );
    setFieldValue('facility', searchParam.get('facility'));
  }, []);

  useEffect(() => {
    setYearOptions(yearFilterArray(facilityDetails, undefined));
  }, []);

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={sortYearOptions(yearOptions)}
                suffixIcon={<YearCalender />}
                onChange={(e) => {
                  setFacilityOpt(
                    facilityDetails?.[e]?.filter((item: any) => {
                      return !item.isLeased;
                    }) ?? []
                  );
                  if (getFieldValue('facility')) {
                    setFields([
                      {
                        name: 'facility',
                        value: undefined,
                        errors: ['Please select facility again']
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {hasUserRole(UserRole.Company_Admin) && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={facilityKeyLabel.key}
                  label={facilityKeyLabel.label}
                  labelCol={{span: 24}}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(facilityKeyLabel.label)
                    }
                  ]}
                  initialValue={searchParam.get('facility')}
                >
                  <Select
                    placeholder={facilityKeyLabel.label}
                    options={
                      facilityOpt?.map((item: any) => {
                        return {label: item.name, value: item._id};
                      }) ?? []
                    }
                  />
                </Form.Item>
              </Col>
            </>
          )}
          {/* <Col className="gutter-row" span={8}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.SUPPLIER_NAME}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(nameFieldLabel.name)
                },
                {
                  max: 100,
                  message: `${nameFieldLabel.name} should be less than 100 characters`
                },
                {
                  min: 3,
                  message: `${nameFieldLabel.name} should be more than 2 characters`
                },
                {
                  pattern: RegEx.businessName,
                  message: CommonErrors.noAllowedSpecChar
                },
                {
                  whitespace: true,
                  message: CommonErrors.mandatory(nameFieldLabel.name)
                }
              ]}
              label={nameFieldLabel.name}
              initialValue={''}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${nameFieldLabel.name}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = eachWordCapOrAllCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.FIRST_NAME}
              label={upstreamLabel.FIRST_NAME}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.FIRST_NAME)
                },
                {
                  max: 100,
                  message: `${upstreamLabel.FIRST_NAME} Should not be more than 100 Characters`
                },
                {
                  min: 2,
                  message: `${upstreamLabel.FIRST_NAME} Should be more than 1 Characters`
                },
                {
                  pattern: RegEx.names,
                  message:
                    'No special characters , Empty spaces or Numbers are Allowed'
                }
              ]}
              validateFirst={true}
              hasFeedback
            >
              <Input
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = FirstCap(target.value);
                }}
                placeholder={`Enter ${upstreamLabel.FIRST_NAME}`}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.LAST_NAME}
              label={upstreamLabel.LAST_NAME}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.LAST_NAME)
                },
                {
                  max: 100,
                  message: `${upstreamLabel.LAST_NAME} Should not be more than 100 Characters`
                },
                {
                  min: 2,
                  message: `${upstreamLabel.LAST_NAME} Should be more than 1 Characters`
                },
                {
                  pattern: RegEx.names,
                  message:
                    'No special characters , Empty spaces or Numbers are Allowed'
                }
              ]}
              validateFirst={true}
              hasFeedback
            >
              <Input
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = FirstCap(target.value);
                }}
                placeholder={`Enter ${upstreamLabel.LAST_NAME}`}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={8}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.MOBILE}
              label={upstreamLabel.MOBILE}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.MOBILE)
                },
                {
                  // eslint-disable-next-line @typescript-eslint/promise-function-async
                  validator(_, value) {
                    if (RegEx.mobile.test(value)) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(
                        new Error(CommonErrors.validField(upstreamLabel.MOBILE))
                      );
                    }
                  }
                }
              ]}
              validateFirst={true}
              hasFeedback
              labelCol={{span: 24}}
            >
              <PhoneNumberInput />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.EMAIL}
              label={upstreamLabel.EMAIL}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.EMAIL)
                },
                {
                  min: 5,
                  message: `${upstreamLabel.EMAIL} Should be more than 4 Characters`
                },
                {
                  max: 320,
                  message: `${upstreamLabel.EMAIL} Should not be more than 320 Characters`
                },
                {
                  pattern: RegEx.email,
                  message: CommonErrors.validField(upstreamLabel.EMAIL)
                }
              ]}
              validateFirst={true}
              hasFeedback
              labelCol={{span: 24}}
            >
              <Input placeholder={`Enter ${upstreamLabel.EMAIL}`} />
            </Form.Item>
          </Col>
        </>
      )}
      {currentStep === 1 && (
        <>
          {/* <Col className="gutter-row" span={8}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.supplierType}
              label={upstreamLabel.supplierType}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.supplierType)
                }
              ]}
              validateFirst={true}
              hasFeedback
              labelCol={{span: 24}}
            >
              <Select
                placeholder={`Choose ${upstreamLabel.supplierType}`}
                options={supplierType}
              />
            </Form.Item>
          </Col>
          {type === 'up' ? (
            <>
              {/* <Col className="gutter-row" span={8}> */}
              <Col className="gutter-row">
                <Form.Item
                  name={upstreamKey.capSupplied}
                  label={upstreamLabel.capSupplied}
                  dependencies={[upstreamKey.rawSupplied]}
                  rules={[
                    // {
                    //   required:
                    //     getFieldValue(upstreamKey.capSupplied)?.length ===
                    //       0 || getFieldValue(upstreamKey.rawSupplied) === 0,
                    //   message: CommonErrors.mandatory(
                    //     upstreamLabel.capSupplied
                    //   )
                    // },
                    ({getFieldValue}) => ({
                      async validator() {
                        if (
                          getFieldValue(upstreamKey.capSupplied)?.length !==
                            0 ||
                          getFieldValue(upstreamKey.rawSupplied)?.length !== 0
                        ) {
                          await Promise.resolve();

                          return;
                        }

                        return await Promise.reject(
                          new Error(
                            CommonErrors.mandatory(
                              `Atleast one of ${upstreamLabel.capSupplied} or ${upstreamLabel.rawSupplied}`
                            )
                          )
                        );
                      }
                    })
                  ]}
                  validateFirst={true}
                  hasFeedback
                  labelCol={{span: 24}}
                  initialValue={[]}
                >
                  <Select
                    showSearch
                    mode="multiple"
                    placeholder={`Choose ${upstreamLabel.capSupplied}`}
                    options={capitalGoods?.filter(
                      (item) => item?.dbName === EfdbName.eeio
                    )}
                    filterOption={filterSelectOptions}
                  />
                </Form.Item>
              </Col>
              {/* <Col className="gutter-row" span={8}> */}
              <Col className="gutter-row">
                <Form.Item
                  name={upstreamKey.rawSupplied}
                  label={upstreamLabel.rawSupplied}
                  dependencies={[upstreamKey.capSupplied]}
                  rules={[
                    // {
                    //   required:
                    //     getFieldValue(upstreamKey.capSupplied)?.length ===
                    //       0 || getFieldValue(upstreamKey.rawSupplied) === 0,
                    //   message: CommonErrors.mandatory(
                    //     upstreamLabel.rawSupplied
                    //   )
                    // },
                    ({getFieldValue}) => ({
                      async validator() {
                        if (
                          getFieldValue(upstreamKey.capSupplied)?.length !==
                            0 ||
                          getFieldValue(upstreamKey.rawSupplied)?.length !== 0
                        ) {
                          await Promise.resolve();

                          return;
                        }

                        return await Promise.reject(
                          new Error(
                            CommonErrors.mandatory(
                              `Atleast one of ${upstreamLabel.rawSupplied} or ${upstreamLabel.capSupplied}`
                            )
                          )
                        );
                      }
                    })
                  ]}
                  validateFirst={true}
                  hasFeedback
                  labelCol={{span: 24}}
                  initialValue={[]}
                  validateTrigger="onChange"
                >
                  <Select
                    showSearch
                    mode="multiple"
                    placeholder={`Choose ${upstreamLabel.rawSupplied}`}
                    options={rawMaterials.filter(
                      (item) => item?.dbName === EfdbName.eeio
                    )}
                    filterOption={filterSelectOptions}
                  />
                </Form.Item>
              </Col>
            </>
          ) : (
            // <Col className="gutter-row" span={8}>
            <Col className="gutter-row">
              <Form.Item
                name={upstreamKey.goodsSupplied}
                label={upstreamLabel.goodsSupplied}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory(upstreamLabel.goodsSupplied)
                  }
                ]}
                validateFirst={true}
                hasFeedback
                labelCol={{span: 24}}
                initialValue={null}
              >
                <Select
                  showSearch
                  mode="multiple"
                  placeholder={`Choose ${upstreamLabel.goodsSupplied}`}
                  options={products.filter(
                    (item) => item?.dbName === EfdbName.eeio
                  )}
                  filterOption={filterSelectOptions}
                />
              </Form.Item>
            </Col>
          )}
        </>
      )}
      {currentStep === 0 && (
        <>
          <Col className="gutter-row" span={24}>
            <Form.Item
              name={upstreamKey.ADDRESS}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.ADDRESS)
                },
                {
                  whitespace: true,
                  message: CommonErrors.mandatory(upstreamLabel.ADDRESS)
                }
              ]}
              label={upstreamLabel.ADDRESS}
              labelCol={{span: 24}}
              initialValue={''}
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${upstreamLabel.ADDRESS}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = FirstCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={colSpan}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.COUNTRY}
              label={upstreamLabel.COUNTRY}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.COUNTRY)
                }
              ]}
              validateFirst
              hasFeedback
            >
              <Select
                showSearch
                className="select"
                placeholder={`Choose ${upstreamLabel.COUNTRY}`}
                filterOption={filterSelectOptions}
                options={countryOptions}
                onChange={(e) => {
                  setCountry(e);
                  if (country) {
                    setFields([
                      {
                        name: upstreamKey.STATE,
                        errors: ['Please Select State again'],
                        value: undefined
                      },
                      {
                        name: upstreamKey.CITY,
                        errors: ['Please Select City again'],
                        value: undefined
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Row gutter={[0, 8]}>
            {/* <Col className="gutter-row" span={colSpan}> */}
            <Col className="gutter-row" span={11}>
              <Form.Item shouldUpdate style={{marginBottom: '0px'}}>
                {({getFieldValue}) => (
                  <Form.Item
                    name={upstreamKey.STATE}
                    label={upstreamLabel.STATE}
                    labelCol={{span: 24}}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(upstreamLabel.STATE)
                      }
                    ]}
                    validateFirst
                    hasFeedback
                  >
                    <Select
                      showSearch
                      className="select"
                      placeholder={`Choose ${upstreamLabel.STATE}`}
                      disabled={!getFieldValue(upstreamKey.COUNTRY)}
                      filterOption={filterSelectOptions}
                      value={data?.[upstreamLabel.STATE] ?? undefined}
                      onChange={() => {
                        if (getFieldValue(upstreamKey.CITY)) {
                          setFields([
                            {
                              name: upstreamKey.CITY,
                              errors: ['Please Select City again'],
                              value: undefined
                            }
                          ]);
                        }
                      }}
                      options={
                        getFieldValue(upstreamKey.COUNTRY)
                          ? stateOptions.filter(
                              (item) =>
                                item?.country_id ===
                                getFieldValue(upstreamKey.COUNTRY)
                            )
                          : [{value: 0, label: ''}]
                      }
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            {/* <Col className="gutter-row" span={colSpan}> */}
            <Col className="gutter-row" span={11} offset={2}>
              <Form.Item shouldUpdate style={{marginBottom: '0px'}}>
                {({getFieldValue}) => (
                  <Form.Item
                    name={upstreamKey.CITY}
                    label={upstreamLabel.CITY}
                    labelCol={{span: 24}}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(upstreamLabel.CITY)
                      }
                    ]}
                    validateFirst
                    hasFeedback
                  >
                    <Select
                      showSearch
                      className="select"
                      placeholder={`Choose ${upstreamLabel.CITY}`}
                      disabled={
                        !(
                          getFieldValue(upstreamKey.STATE) &&
                          getFieldValue(upstreamKey.COUNTRY)
                        )
                      }
                      value={
                        cityOptions.length > 0
                          ? data?.[upstreamLabel.CITY]
                          : undefined
                      }
                      filterOption={filterSelectOptions}
                      options={
                        getFieldValue(upstreamKey.STATE)
                          ? cityOptions.filter(
                              (item) =>
                                item?.state_id ===
                                getFieldValue(upstreamKey.STATE)
                            )
                          : [{value: 0, label: ''}]
                      }
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>
          {/* <Col className="gutter-row" span={colSpan}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.PINCODE}
              label={upstreamLabel.PINCODE}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(upstreamLabel.PINCODE)
                },
                {
                  pattern: RegEx.pinCode,
                  message: 'Special characters are not allowed.'
                },
                {
                  whitespace: true,
                  message: CommonErrors.mandatory(upstreamLabel.PINCODE)
                }
              ]}
              validateFirst
              hasFeedback
            >
              <Input
                className="input"
                placeholder={`Enter ${upstreamLabel.PINCODE}`}
                onInput={(e) => {
                  const target = e.target as HTMLInputElement;
                  target.value = AllCap(target.value);
                }}
              />
            </Form.Item>
          </Col>
        </>
      )}

      {/* {type === 'down' && country === 101 && (
        <Col className="gutter-row" span={6}>
          <Form.Item
            name={OPKey.TAX}
            label={OPLabel.GST}
            rules={[
              {
                required: true,
                message: CommonErrors.mandatory(OPLabel.GST)
              },
              {
                pattern: RegEx.GST,
                message: CommonErrors.validDigitField(OPLabel.GST, 15)
              }
            ]}
            validateFirst={true}
            hasFeedback
            labelCol={{span: 24}}
          >
            <Input
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.value = target.value.toUpperCase();
              }}
              placeholder={`Enter ${OPLabel.GST}`}
            />
          </Form.Item>
        </Col>
      )} */}
      {currentStep === 2 && type === 'up' && (
        <>
          {/* <Col className="gutter-row" span={colSpan}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.ENV_CRITERIA}
              label={upstreamLabel.ENV_CRITERIA}
              labelCol={{span: 24}}
              rules={[]}
              initialValue={false}
              tooltip={{
                icon: <InfoCircleOutlined />,

                title: supplierInfo?.screenSupplier
              }}
              className="facilityFormSwitch"
              validateFirst
              hasFeedback
            >
              <Radio.Group
                options={yesNo}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={colSpan}> */}
          <Col className="gutter-row">
            <Form.Item
              name={upstreamKey.SUSTAINABLE_SOURCING}
              label={upstreamLabel.SUSTAINABLE_SOURCING}
              labelCol={{span: 24}}
              initialValue={false}
              tooltip={{
                icon: <InfoCircleOutlined />,
                title: InformativeText.supplierInfo.susSource
              }}
              className="facilityFormSwitch"
              validateFirst
              hasFeedback
            >
              <Radio.Group
                options={yesNo}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </Col>
        </>
      )}
      {currentStep === 1 && (
        // <Col className="gutter-row" span={6}>
        <Col className="gutter-row">
          <Form.Item dependencies={[upstreamKey.COUNTRY]} noStyle>
            {({getFieldValue}) =>
              getFieldValue(upstreamKey.COUNTRY) === 101 && (
                <Form.Item
                  name={OPKey.TAX}
                  label={OPLabel.GST}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(OPLabel.GST)
                    },
                    {
                      pattern: RegEx.GST,
                      message: CommonErrors.validDigitField(OPLabel.GST, 15)
                    }
                  ]}
                  validateFirst={true}
                  hasFeedback
                  labelCol={{span: 24}}
                  initialValue={''}
                >
                  <Input
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value.toUpperCase();
                    }}
                    placeholder={`Enter ${OPplaceholder.GST}`}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
        </Col>
      )}

      {/* <Col className="gutter-row">
          <Form.Item
            name={upstreamKey.PARTNER_TYPE}
            label={nameFieldLabel.nameType}
            initialValue={undefined}
            rules={[
              {
                required: true,
                message: CommonErrors.mandatory(nameFieldLabel.nameType)
              }
            ]}
            labelCol={{span: 24}}
          >
            <Select
              className="select"
              placeholder={nameFieldLabel.nameType}
              options={partnerType}
            />
          </Form.Item>
        </Col> */}
    </>
  );
};

export default SupplierInfo;
